// ─────────────────────────────────────────────────────────────────────────────
// BELBIN IPS SCORING ENGINE
// Input:  answers — { sectionId: { statementId: points } }
//         e.g. { A: { A1:3, A2:2, A3:0, A4:1, A5:0, A6:2, A7:1, A8:1 } }
// Output: { scales, clusterScores, primary, secondary, tertiary, ranked }
// ─────────────────────────────────────────────────────────────────────────────
import { SECTIONS, POINTS_PER_SECTION } from "./questions.js";
import { ROLES, CLUSTERS } from "./factors.js";

export function scoreBelbin(answers) {
  // Sum all points per role across all sections
  const roleTotals = {};
  ROLES.forEach(r => { roleTotals[r.id] = 0; });

  SECTIONS.forEach(section => {
    const sectionAnswers = answers[section.id] || {};
    section.statements.forEach(stmt => {
      const pts = sectionAnswers[stmt.id] || 0;
      roleTotals[stmt.role] = (roleTotals[stmt.role] || 0) + pts;
    });
  });

  // Max possible per role = 10 points × 7 sections = 70
  // But each section only has 1-2 statements per role, so realistic max ~20-35
  // Normalise to 1-10 scale based on actual max across all roles
  const maxTotal = Math.max(...Object.values(roleTotals), 1);
  
  const scales = {};
  ROLES.forEach(r => {
    const raw = roleTotals[r.id];
    // Scale: 0 points → 1, maxTotal → 10
    scales[r.id] = Math.max(1, Math.min(10, Math.round((raw / maxTotal) * 9) + 1));
  });

  // Also store raw totals for display
  const rawScores = { ...roleTotals };

  // Rank roles by raw score (more meaningful than normalised)
  const ranked = Object.entries(rawScores)
    .sort((a,b) => b[1]-a[1])
    .map(([id, raw]) => ({ id, raw, score: scales[id], role: ROLES.find(r=>r.id===id) }));

  const primary   = ranked[0]?.id || null;
  const secondary = ranked[1]?.id || null;
  const tertiary  = ranked[2]?.id || null;
  const least     = ranked[ranked.length-1]?.id || null;

  // Cluster scores
  const clusterScores = {};
  CLUSTERS.forEach(c => {
    const vals = c.roles.map(r => scales[r]);
    clusterScores[c.id] = +(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1);
  });

  const actionScore = clusterScores["Action"]  || 5;
  const socialScore = clusterScores["Social"]  || 5;
  const thinkScore  = clusterScores["Thinking"]|| 5;

  const perfAvg = +actionScore.toFixed(1);
  const potAvg  = +(socialScore*0.5 + thinkScore*0.5).toFixed(1);
  const perfBox = perfAvg <= 4 ? 1 : perfAvg <= 7 ? 2 : 3;
  const potBox  = potAvg  <= 4 ? 1 : potAvg  <= 7 ? 2 : 3;

  return {
    scales,
    rawScores,
    clusterScores,
    primary, secondary, tertiary, least,
    ranked,
    validityFlag: null,
    perfAvg, potAvg, perfBox, potBox,
    axes: { x: perfAvg, y: potAvg },
  };
}

// Validate a section's answers — must sum to exactly POINTS_PER_SECTION
export function validateSection(sectionAnswers, section) {
  const total = section.statements.reduce((sum, stmt) => sum + (sectionAnswers[stmt.id] || 0), 0);
  return total === 10;
}
